import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { parseISO } from "date-fns";

import { useGetOpenShiftsForDates } from "../../Shift/Open/useGetOpenShiftsForDates";
import { useGetShiftDiscoveryDefaultDateRange } from "../../utils/useGetShiftDiscoveryDefaultDateRange";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { isBonusEligibleShift } from "./isBonusEligibleShift";

interface UseBonusEligibleShiftShoppingDataProps {
  /**
   * Minimum hours a shift must have been open for (posted ago) to be
   * bonus-eligible. Sourced from the `2026-04-configure-bonus-shift-shopping`
   * feature-flag config (`minimumPostedHours`).
   */
  minimumPostedHours: number;
}

export function useBonusEligibleShiftShoppingData(props: UseBonusEligibleShiftShoppingDataProps) {
  const { minimumPostedHours } = props;

  const { dates: dateStrings, distance, includedStates } = useShiftDiscoveryUserFiltersContext();
  const workerGeoLocation = useWorkerGeoLocation();
  const defaultDateRange = useGetShiftDiscoveryDefaultDateRange();

  const dates = dateStrings.map((dateString) => parseISO(dateString));

  const { shifts, ...rest } = useGetOpenShiftsForDates({
    dates,
    dateRange: defaultDateRange,
    filter: {
      area: {
        latitude: workerGeoLocation?.latitude ?? 0,
        longitude: workerGeoLocation?.longitude ?? 0,
        radiusInMiles: distance,
      },
      ...(includedStates?.length && { includedStates }),
    },
    enabled: isDefined(workerGeoLocation),
  });

  // Urgent shifts are dropped client-side: we intentionally do not send
  // `filter[isUrgent]=false` because the BFF's Zod `z.coerce.boolean()` on
  // that query param coerces the string `"false"` to `true` (any non-empty
  // string is truthy), which flips the filter into "urgent-only" mode and
  // returns zero non-urgent shifts.
  const bonusEligibleShifts = shifts.filter((shift) => {
    if (shift.attributes.isUrgent) {
      return false;
    }

    const createdAt =
      "createdAt" in shift.attributes && typeof shift.attributes.createdAt === "string"
        ? shift.attributes.createdAt
        : undefined;

    return isBonusEligibleShift({
      createdAt,
      minimumPostedHours,
    });
  });

  return {
    ...rest,
    shifts: bonusEligibleShifts,
  };
}
